import { useEffect, useRef, useState } from 'react';

import {
  type ForeignDataTree,
  foreignDataToTree,
  parseForeignData,
} from '../Pages/Graph/src/data/foreign-data';

type ParserState = {
  isLoading: boolean;
  data: ForeignDataTree | null;
};

const isLikelySlowForParseForeignData = (data: unknown): boolean => {
  if (!data || typeof data !== 'object') return false;

  // Check for deep nesting with single keys (expensive for zAnyMessages/zAnyDocument)
  const checkDepth = (obj: unknown, depth = 0): number => {
    if (depth > 50) return depth; // Bail out early
    if (!obj || typeof obj !== 'object') return depth;

    const keys = Object.keys(obj as object);
    if (keys.length === 1) {
      return checkDepth((obj as Record<string, unknown>)[keys[0]!], depth + 1);
    }
    return depth;
  };

  const maxDepth = checkDepth(data);
  if (maxDepth > 10) return true; // Deep single-key nesting is slow

  // Check for large objects (expensive for zod validation)
  const keyCount = Object.keys(data as object).length;
  if (keyCount > 100) return true;

  // Check for arrays with many items
  if (Array.isArray(data) && data.length > 1000) return true;

  return false;
};

export function useForeignDataParser(
  data?: unknown,
  path: string[] = [],
  variant: 'partial' | 'simple' = 'partial'
) {
  const workerRef = useRef<Worker | null>(null);
  const [state, setState] = useState<ParserState>({
    isLoading: false,
    data: null,
  });
  const shouldUseWorker = isLikelySlowForParseForeignData(data);

  useEffect(() => {
    // Create worker
    workerRef.current = new Worker(
      new URL('../workers/foreignDataParser.ts', import.meta.url),
      { type: 'module' }
    );

    // Handle messages from worker
    workerRef.current.onmessage = (e: MessageEvent) => {
      setState({
        isLoading: false,
        data: e.data.result,
      });
    };

    // Cleanup
    return () => {
      workerRef.current?.terminate();
    };
  }, []);

  // Auto-parse when data changes
  useEffect(() => {
    if (!data) {
      setState({ isLoading: false, data: null });
      return;
    }

    // Use heuristic to determine if we should parse sync or async
    const shouldUseWorker = isLikelySlowForParseForeignData(data);

    if (!workerRef.current) return;

    if (shouldUseWorker) {
      setState({ isLoading: true, data: null });
      workerRef.current.postMessage({ data, path, variant });
    }
  }, [data, path, variant]);

  // If data is small based on heuristic, parse synchronously and return the result
  if (!shouldUseWorker) {
    const result = foreignDataToTree({
      data: parseForeignData(data, path, variant),
    });
    return {
      isLoading: false,
      data: result,
    };
  }

  return {
    ...state,
  };
}
